// TanStack Query client configuration
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import {
  isAuthenticationRequiredError,
  handleAssistantAuthenticationError,
} from './auth-error-handler'

// Check if current user is an assistant based on storage
function isAssistantContext(): boolean {
  if (typeof window !== 'undefined') {
    // If assistant auth exists in localStorage, user is an assistant
    const hasAssistantAuth = localStorage.getItem('digimataam_assistant_auth') !== null
    // Make sure we're not on owner/staff login pages
    const isOwnerPage =
      window.location.pathname.includes('/login') ||
      window.location.pathname.includes('/owner') ||
      window.location.pathname.includes('/settings')

    return hasAssistantAuth && !isOwnerPage
  }
  return false
}

// Global error handler for authentication errors
function handleGlobalError(error: unknown) {
  if (isAuthenticationRequiredError(error) && isAssistantContext()) {
    handleAssistantAuthenticationError(error)
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      handleGlobalError(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleGlobalError(error)
    },
  }),
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache time: 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests
      retry: 1,
      // Only refetch on window focus in production (we have realtime for updates)
      refetchOnWindowFocus: import.meta.env.PROD,
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
      // Don't refetch on reconnect in development (reduces requests during hot reload)
      refetchOnReconnect: import.meta.env.PROD,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
})

// Query keys factory for type-safe and consistent query keys
export const queryKeys = {
  // Auth
  auth: {
    user: () => ['auth', 'user'] as const,
    session: () => ['auth', 'session'] as const,
    staff: () => ['auth', 'staff'] as const,
    assistant: () => ['auth', 'assistant'] as const,
  },
  // Users
  users: {
    all: () => ['users'] as const,
    list: (filters?: Record<string, unknown>) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  // Restaurants
  restaurants: {
    all: () => ['restaurants'] as const,
    list: () => ['restaurants', 'list'] as const,
    detail: (id: string) => ['restaurants', 'detail', id] as const,
  },
  // Staff
  staff: {
    all: () => ['staff'] as const,
    list: (restaurantId: string) => ['staff', 'list', restaurantId] as const,
    detail: (id: string) => ['staff', 'detail', id] as const,
  },
  // Tables
  tables: {
    all: () => ['tables'] as const,
    list: (restaurantId: string) => ['tables', 'list', restaurantId] as const,
    detail: (id: string) => ['tables', 'detail', id] as const,
  },
  // Ingredients
  ingredients: {
    all: () => ['ingredients'] as const,
    list: (filters?: Record<string, unknown>) => ['ingredients', 'list', filters] as const,
    detail: (id: string) => ['ingredients', 'detail', id] as const,
    search: (query: string) => ['ingredients', 'search', query] as const,
  },
  // Inventory
  inventory: {
    all: () => ['inventory'] as const,
    list: (filters?: Record<string, unknown>) => ['inventory', 'list', filters] as const,
    detail: (id: string) => ['inventory', 'detail', id] as const,
    lowStock: (filters?: Record<string, unknown>) => ['inventory', 'low-stock', filters] as const,
  },
  // Menu Items
  menuItems: {
    all: () => ['menu-items'] as const,
    list: (filters?: Record<string, unknown>) => ['menu-items', 'list', filters] as const,
    detail: (id: string) => ['menu-items', 'detail', id] as const,
    withIngredients: (id: string) => ['menu-items', 'with-ingredients', id] as const,
    raw: (restaurantId: string) => ['menu-items', 'raw', restaurantId] as const, // Raw RPC data for useOrders
    // Batch-loaded data for performance optimization
    allSpecialties: (restaurantId: string) => ['menu-item-specialties', 'restaurant', restaurantId] as const,
    allOptions: (restaurantId: string) => ['menu-item-options-all', 'restaurant', restaurantId] as const,
  },
  // Orders
  orders: {
    all: () => ['orders'] as const,
    list: (filters?: Record<string, unknown>) => ['orders', 'list', filters] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
    byTable: (tableId: string) => ['orders', 'by-table', tableId] as const,
    byServer: (serverId: string) => ['orders', 'by-server', serverId] as const,
    // Static data that changes infrequently - cache separately
    orderItems: (restaurantId: string) => ['orders', 'order-items', restaurantId] as const,
  },
  // Notifications (removed - now client-side only)
  // notifications: {
  //   all: () => ['notifications'] as const,
  //   list: (userId: string) => ['notifications', 'list', userId] as const,
  //   unread: (userId: string) => ['notifications', 'unread', userId] as const,
  // },
  // Analytics
  analytics: {
    all: () => ['analytics'] as const,
    tableStats: (params: Record<string, unknown>) => ['analytics', 'table-stats', params] as const,
    period: (period: string, params: Record<string, unknown>) =>
      ['analytics', 'period', period, params] as const,
    popular: (params: Record<string, unknown>) => ['analytics', 'popular', params] as const,
  },
  // Accounting
  accounting: {
    all: () => ['accounting'] as const,
    expenseCategories: (restaurantId: string) =>
      ['accounting', 'expense-categories', restaurantId] as const,
    employees: (restaurantId: string) => ['accounting', 'employees', restaurantId] as const,
    recurringExpenses: (restaurantId: string) =>
      ['accounting', 'recurring-expenses', restaurantId] as const,
    expenses: (restaurantId: string, filters?: Record<string, unknown>) =>
      ['accounting', 'expenses', restaurantId, filters] as const,
    financialSummary: (restaurantId: string, startDate: string, endDate: string) =>
      ['accounting', 'financial-summary', restaurantId, startDate, endDate] as const,
    expenseBreakdown: (restaurantId: string, startDate: string, endDate: string) =>
      ['accounting', 'expense-breakdown', restaurantId, startDate, endDate] as const,
    salarySummary: (restaurantId: string) =>
      ['accounting', 'salary-summary', restaurantId] as const,
    recurringExpensesSummary: (restaurantId: string) =>
      ['accounting', 'recurring-expenses-summary', restaurantId] as const,
  },
} as const
